import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Package, ShoppingCart, Users, IndianRupee, KeyRound, Globe, ShieldCheck, Ban, Paintbrush, Wallet, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, errMsg, fileUrl } from "../../lib/api";
import { PageHeader, StatusBadge, StatCard, LoadingRows, ConfirmDialog } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import PwaSettings from "../../components/PwaSettings";
import { mergePwa } from "../../lib/pwa";

const DEFAULT_PAY = { gateway_enabled: true, whatsapp_enabled: true, upi_enabled: false, upi_id: "", upi_name: "", upi_qr: "" };

export default function WebsiteDetail() {
  const { wid } = useParams();
  const navigate = useNavigate();
  const [w, setW] = useState(null);
  const [stats, setStats] = useState(null);
  const [themes, setThemes] = useState([]);
  const [info, setInfo] = useState({});
  const [domain, setDomain] = useState("");
  const [waNumber, setWaNumber] = useState("");
  const [branding, setBranding] = useState({});
  const [newCreds, setNewCreds] = useState(null);
  const [pwaCfg, setPwaCfg] = useState(null);
  const [pwaBusy, setPwaBusy] = useState(false);
  const [pay, setPay] = useState(DEFAULT_PAY);

  const load = useCallback(() => {
    api.get(`/partner/websites/${wid}`).then((r) => { setW(r.data); setInfo(r.data.business_info || {}); setDomain(r.data.domain?.custom || ""); setWaNumber(r.data.settings?.whatsapp_number || ""); setPwaCfg(mergePwa(r.data)); setPay({ ...DEFAULT_PAY, ...(r.data.settings?.payments || {}) }); });
    api.get("/partner/branding").then((r) => setBranding(r.data.branding || {}));
    api.get(`/partner/websites/${wid}/stats`).then((r) => setStats(r.data));
    api.get("/partner/themes").then((r) => setThemes(r.data));
  }, [wid]);
  useEffect(() => { load(); }, [load]);

  if (!w || !stats) return <LoadingRows rows={4} />;

  const saveInfo = async () => {
    try {
      await api.patch(`/partner/websites/${wid}`, { business_info: info });
      toast.success("Business info saved"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const switchTheme = async (t) => {
    try {
      await api.patch(`/partner/websites/${wid}`, { theme_id: t.id, settings: { ...w.settings, colors: t.colors } });
      toast.success(`Theme switched to ${t.name} — all data preserved`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveWhatsApp = async () => {
    const v = waNumber.trim();
    if (v && !/^\+?[0-9]{8,15}$/.test(v)) { toast.error("Invalid WhatsApp number — use country code + digits, e.g. +919876543210"); return; }
    try {
      await api.patch(`/partner/websites/${wid}`, { settings: { ...w.settings, whatsapp_number: v } });
      toast.success("WhatsApp order number updated"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveDomain = async () => {
    try {
      await api.patch(`/partner/websites/${wid}`, { domain: { custom: domain } });
      toast.success("Domain saved — pending verification"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const savePay = async () => {
    const upi = (pay.upi_id || "").trim();
    if (pay.upi_enabled && upi && (!upi.includes("@") || upi.length < 5)) { toast.error("Invalid UPI ID — e.g. store@okhdfcbank"); return; }
    try {
      await api.patch(`/partner/websites/${wid}`, { settings: { ...w.settings, whatsapp_number: waNumber, payments: pay } });
      toast.success("Payment settings saved"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const uploadQr = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      setPay((p) => ({ ...p, upi_qr: data.url }));
      toast.success("QR uploaded — click Save to apply");
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const savePwa = async () => {
    setPwaBusy(true);
    try {
      await api.patch(`/partner/websites/${wid}`, { pwa: pwaCfg });
      toast.success("App settings saved"); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setPwaBusy(false); }
  };

  const verifyDomain = async () => {
    try {
      await api.post(`/partner/websites/${wid}/domain/verify`);
      toast.success("Domain verified — HTTPS is issued automatically by the platform within a few minutes"); load();
    } catch (e) { toast.error(errMsg(e)); load(); }
  };

  const resetOwner = async () => {
    try {
      const { data } = await api.post(`/partner/websites/${wid}/owner/reset`);
      setNewCreds(data);
      toast.success("Owner password reset");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const suspend = async () => {
    await api.delete(`/partner/websites/${wid}`);
    toast.success("Website suspended — data preserved");
    navigate("/partner/websites");
  };

  return (
    <div data-testid="website-detail">
      <button onClick={() => navigate("/partner/websites")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
        <ArrowLeft className="h-4 w-4" /> All websites
      </button>
      <PageHeader title={w.name} subtitle={`/site/${w.slug} · ${w.type} · created ${new Date(w.created_at).toLocaleDateString("en-IN")}`} testid="website-name">
        <div className="flex gap-2">
          <Button data-testid="open-editor-button" onClick={() => navigate(`/partner/editor/${wid}`)} className="rounded-full font-bold">
            <Paintbrush className="h-4 w-4 mr-2" /> Visual Editor
          </Button>
          <Button data-testid="open-store-button" variant="outline" onClick={() => window.open(`/site/${w.slug}`, "_blank")} className="rounded-full font-bold">
            <ExternalLink className="h-4 w-4 mr-2" /> Open Store
          </Button>
          {w.status === "active" && (
            <ConfirmDialog testid="suspend-website" title="Suspend website?" description="The storefront goes offline but all products, orders and customers are preserved."
              onConfirm={suspend}
              trigger={<Button variant="outline" className="rounded-full font-bold text-amber-600 border-amber-500/30"><Ban className="h-4 w-4 mr-2" /> Suspend</Button>} />
          )}
        </div>
      </PageHeader>

      <div className="flex items-center gap-3 mb-8">
        <StatusBadge status={w.status} />
        <StatusBadge status={w.domain?.status || "not_connected"} />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <StatCard icon={Package} label="Products" value={stats.products} testid="wd-products" />
        <StatCard icon={ShoppingCart} label="Orders" value={stats.orders} testid="wd-orders" delay={0.05} />
        <StatCard icon={Users} label="Customers" value={stats.customers} testid="wd-customers" delay={0.1} />
        <StatCard icon={IndianRupee} label="Revenue" value={fmtINR(stats.revenue)} testid="wd-revenue" delay={0.15} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="rounded-full" data-testid="website-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-full">Overview</TabsTrigger>
          <TabsTrigger value="theme" data-testid="tab-theme" className="rounded-full">Theme</TabsTrigger>
          <TabsTrigger value="domain" data-testid="tab-domain" className="rounded-full">Domain</TabsTrigger>
          <TabsTrigger value="app" data-testid="tab-app" className="rounded-full">App / PWA</TabsTrigger>
          <TabsTrigger value="access" data-testid="tab-access" className="rounded-full">Access</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-8">
          <div className="bg-card border border-border rounded-md p-8 max-w-2xl space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="overline-label">Phone</Label>
                <Input data-testid="info-phone" value={info.phone || ""} onChange={(e) => setInfo({ ...info, phone: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="overline-label">Email</Label>
                <Input data-testid="info-email" value={info.email || ""} onChange={(e) => setInfo({ ...info, email: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label className="overline-label">Description</Label>
              <Input data-testid="info-description" value={info.description || ""} onChange={(e) => setInfo({ ...info, description: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="overline-label">Address</Label>
              <Input data-testid="info-address" value={info.address || ""} onChange={(e) => setInfo({ ...info, address: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="overline-label">WhatsApp Order Number</Label>
              <div className="flex gap-2">
                <Input data-testid="wa-number-input" value={waNumber} onChange={(e) => setWaNumber(e.target.value)} placeholder="+919876543210" className="font-mono" />
                <Button data-testid="save-wa-button" onClick={saveWhatsApp} variant="outline" className="rounded-full font-bold">Save</Button>
              </div>
              <p className="text-xs text-muted-foreground">Customers can send orders to this WhatsApp number from checkout. Include country code. Applies only to this website.</p>
            </div>
            <div className="border border-border rounded-md p-5 space-y-4" data-testid="partner-payment-settings">
              <div className="flex items-center gap-2.5">
                <Wallet className="h-4 w-4" />
                <div><p className="text-sm font-extrabold">Checkout / Payment Settings</p>
                  <p className="text-xs text-muted-foreground">Only enabled methods appear on this website's checkout.</p></div>
              </div>
              {[
                ["gateway_enabled", "Online Payment Gateway", "Cards, netbanking & UPI via the payment gateway."],
                ["whatsapp_enabled", "WhatsApp Ordering", "Full order sent to the WhatsApp number above."],
                ["upi_enabled", "UPI / QR Payment", "Show this website's own UPI ID and QR at checkout."],
              ].map(([k, label, hint]) => (
                <div key={k} className="flex items-center justify-between gap-4">
                  <div><p className="text-sm font-bold">{label}</p><p className="text-xs text-muted-foreground">{hint}</p></div>
                  <Switch data-testid={`partner-pay-toggle-${k}`} checked={!!pay[k]} onCheckedChange={(v) => setPay({ ...pay, [k]: v })} />
                </div>
              ))}
              {pay.upi_enabled && (
                <div className="space-y-3 pt-3 border-t border-border">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Input data-testid="partner-upi-name" value={pay.upi_name} onChange={(e) => setPay({ ...pay, upi_name: e.target.value })} placeholder="Payment name (payee)" className="h-10 text-sm" />
                    <Input data-testid="partner-upi-id" value={pay.upi_id} onChange={(e) => setPay({ ...pay, upi_id: e.target.value })} placeholder="UPI ID — store@okhdfcbank" className="h-10 text-sm font-mono" />
                  </div>
                  <div className="flex items-center gap-3">
                    {pay.upi_qr && <img src={fileUrl(pay.upi_qr)} alt="UPI QR" className="h-20 w-20 object-contain rounded-md border border-border bg-white p-1.5" data-testid="partner-upi-qr-preview" />}
                    <label className="h-9 px-4 rounded-full border border-border text-xs font-bold flex items-center gap-1.5 cursor-pointer hover:bg-muted transition-colors" data-testid="partner-upi-qr-upload">
                      <Upload className="h-3.5 w-3.5" /> Upload QR<input type="file" accept="image/*" className="hidden" onChange={uploadQr} />
                    </label>
                  </div>
                </div>
              )}
              <Button data-testid="save-pay-button" onClick={savePay} variant="outline" className="rounded-full font-bold">Save Payment Settings</Button>
            </div>
            <Button data-testid="save-info-button" onClick={saveInfo} className="rounded-full font-bold">Save Changes</Button>
          </div>
        </TabsContent>

        <TabsContent value="theme" className="mt-8">
          {w.theme && (
            <div className="flex items-center gap-4 border border-border rounded-md p-5 mb-6 max-w-2xl bg-card">
              <img src={w.theme.preview} alt="" className="h-16 w-24 object-cover rounded" />
              <div><p className="overline-label">Current theme</p><p className="font-display font-bold text-lg">{w.theme.name}</p></div>
            </div>
          )}
          <p className="text-sm text-muted-foreground mb-4 max-w-2xl">Switching themes changes presentation only — products, orders, customers and settings stay intact.</p>
          <div className="grid sm:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {themes.filter((t) => t.id !== w.theme_id).map((t) => (
              <button key={t.id} data-testid={`switch-theme-${t.id}`} onClick={() => switchTheme(t)}
                className="text-left border border-border rounded-md overflow-hidden hover:border-foreground/50 transition-colors group">
                <div className="aspect-[16/9] bg-muted overflow-hidden">
                  {t.preview && <img src={t.preview} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                </div>
                <div className="p-3"><p className="text-sm font-bold">{t.name}</p><p className="text-xs text-muted-foreground">{t.category}</p></div>
              </button>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="domain" className="mt-8">
          <div className="bg-card border border-border rounded-md p-8 max-w-2xl space-y-6">
            <div>
              <p className="overline-label mb-2">Platform subdomain</p>
              <div className="flex items-center gap-2 border border-border rounded-md px-4 py-3 bg-muted/50">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <span className="font-mono text-sm" data-testid="subdomain-url">{window.location.origin}/site/{w.slug}</span>
              </div>
            </div>
            <div>
              <p className="overline-label mb-2">Partner subdomain (auto-assigned)</p>
              <div className="flex items-center gap-2 border border-border rounded-md px-4 py-3 bg-muted/50">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <span className="font-mono text-sm" data-testid="partner-subdomain-url">{w.slug}.{branding.domain || "your-agency-domain.com"}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Uses the primary domain from your Branding settings. If the client has no custom domain, this partner-branded subdomain is their public address.</p>
            </div>
            <div>
              <p className="overline-label mb-2">Custom domain</p>
              <div className="flex gap-2">
                <Input data-testid="custom-domain-input" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="yourbusiness.com" className="font-mono" />
                <Button data-testid="save-domain-button" onClick={saveDomain} variant="outline" className="rounded-full font-bold">Save</Button>
                {w.domain?.status === "pending" && (
                  <Button data-testid="verify-domain-button" onClick={verifyDomain} className="rounded-full font-bold">
                    <ShieldCheck className="h-4 w-4 mr-2" /> Verify
                  </Button>
                )}
              </div>
            </div>
            {w.domain?.status !== "not_connected" && (
              <div className="border border-border rounded-md p-5 bg-muted/30">
                <p className="overline-label mb-3">DNS instructions</p>
                <div className="font-mono text-xs space-y-1.5">
                  <p>Type: <span className="font-bold">CNAME</span></p>
                  <p>Host: <span className="font-bold">{domain || "www"}</span></p>
                  <p>Value: <span className="font-bold">{window.location.hostname}</span></p>
                  <p className="text-muted-foreground pt-2">HTTPS is issued automatically once verified. DNS propagation can take up to 24 hours.</p>
                </div>
                {w.domain?.last_check && (
                  <p className="text-xs text-muted-foreground mt-3" data-testid="domain-last-check">
                    Last check: {w.domain.last_check.reason}
                  </p>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="access" className="mt-8">
          <div className="bg-card border border-border rounded-md p-8 max-w-2xl space-y-5">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-accent/15 text-accent flex items-center justify-center font-display font-bold text-lg">
                {w.owner?.name?.[0]?.toUpperCase()}
              </div>
              <div>
                <p className="font-bold" data-testid="owner-name">{w.owner?.name}</p>
                <p className="text-sm text-muted-foreground" data-testid="owner-email">{w.owner?.email}</p>
              </div>
            </div>
            <Button data-testid="reset-owner-password" onClick={resetOwner} variant="outline" className="rounded-full font-bold">
              <KeyRound className="h-4 w-4 mr-2" /> Reset Owner Password
            </Button>
            {newCreds && (
              <div data-testid="new-owner-creds" className="border border-emerald-500/30 bg-emerald-500/5 rounded-md p-5 font-mono text-sm space-y-1">
                <p>Email: <span className="font-bold">{newCreds.email}</span></p>
                <p>New password: <span className="font-bold">{newCreds.password}</span></p>
                <p className="text-xs text-muted-foreground font-sans pt-1">Shown once — share it with your client securely.</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="app" className="mt-8" data-testid="partner-pwa-tab">
          {pwaCfg && <PwaSettings value={pwaCfg} onChange={setPwaCfg} onSave={savePwa} busy={pwaBusy} storeSlug={w.slug} />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
